import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { DoubtStatusBadge, DoubtVisibilityBadge, DOUBT_STATUS_FILTERS } from "@/lib/doubts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, MessageCircleQuestion, MessageSquare, Lock, Users } from "lucide-react";

export default function StudentDoubts() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [courses, setCourses] = useState([]);
    const [courseFilter, setCourseFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");

    const [open, setOpen] = useState(false);
    const [lessons, setLessons] = useState([]);
    const [form, setForm] = useState({ course_id: "", lesson_id: "", title: "", body: "", visibility: "shared" });
    const [posting, setPosting] = useState(false);

    const load = async () => {
        const params = new URLSearchParams();
        if (courseFilter) params.append("course_id", courseFilter);
        if (statusFilter !== "ALL") params.append("status", statusFilter);
        const { data } = await api.get(`/doubts?${params}`);
        setItems(data.items);
    };
    useEffect(() => { load(); }, [courseFilter, statusFilter]); // eslint-disable-line
    useEffect(() => { api.get("/courses").then(r => setCourses(r.data.items)).catch(() => {}); }, []);

    useEffect(() => {
        if (!form.course_id) { setLessons([]); return; }
        api.get(`/courses/${form.course_id}`).then(({ data }) => {
            const flat = (data.modules || []).flatMap(m =>
                (m.lessons || []).map(l => ({ id: l.id, label: `${m.title} · ${l.title}` }))
            );
            setLessons(flat);
        }).catch(() => setLessons([]));
    }, [form.course_id]);

    const courseName = (id) => courses.find(c => c.id === id)?.title;

    const resetForm = () => setForm({ course_id: "", lesson_id: "", title: "", body: "", visibility: "shared" });

    const post = async () => {
        if (!form.course_id || !form.title.trim() || !form.body.trim()) return;
        setPosting(true);
        try {
            await api.post("/doubts", {
                course_id: form.course_id,
                lesson_id: form.lesson_id || null,
                title: form.title.trim(),
                body: form.body.trim(),
                visibility: form.visibility,
            });
            toast.success("Doubt posted");
            setOpen(false);
            resetForm();
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not post your doubt");
        } finally {
            setPosting(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="student-doubts-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Q&amp;A</div>
                    <h1 className="font-display text-3xl lg:text-4xl mt-1">Doubts</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Ask your batch faculty. Shared doubts build a knowledge base for your whole batch; private ones are just between you and staff.
                    </p>
                </div>
                <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
                    <DialogTrigger asChild>
                        <Button className="mx-btn-primary" data-testid="post-doubt-btn"><Plus className="h-4 w-4 mr-2" /> Post a doubt</Button>
                    </DialogTrigger>
                    <DialogContent className="max-h-[85vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>Post a doubt</DialogTitle>
                            <DialogDescription>Any faculty on your batch can answer — not just your course's assigned one.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div>
                                <Label>Course</Label>
                                <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v, lesson_id: "" })}>
                                    <SelectTrigger data-testid="doubt-course-select"><SelectValue placeholder="Select a course" /></SelectTrigger>
                                    <SelectContent>{courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            {form.course_id && lessons.length > 0 && (
                                <div>
                                    <Label>Lesson <span className="text-muted-foreground font-normal">(optional)</span></Label>
                                    <Select value={form.lesson_id || "NONE"} onValueChange={(v) => setForm({ ...form, lesson_id: v === "NONE" ? "" : v })}>
                                        <SelectTrigger data-testid="doubt-lesson-select"><SelectValue placeholder="No specific lesson" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="NONE">No specific lesson</SelectItem>
                                            {lessons.map(l => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            <div>
                                <Label>Title</Label>
                                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                                    placeholder="e.g. Why does useEffect run twice?" data-testid="doubt-title-input" />
                            </div>
                            <div>
                                <Label>Details</Label>
                                <Textarea rows={4} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
                                    placeholder="Explain what you tried and where you got stuck…" data-testid="doubt-body-input" />
                            </div>
                            <div>
                                <Label>Visibility</Label>
                                <RadioGroup value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })} className="mt-2 gap-3">
                                    <label className="flex items-start gap-3 p-3 rounded-xl border border-border cursor-pointer has-[[data-state=checked]]:border-[#726AF0]">
                                        <RadioGroupItem value="shared" id="vis-shared" data-testid="doubt-visibility-shared" />
                                        <div>
                                            <div className="flex items-center gap-1.5 font-semibold text-sm"><Users className="h-3.5 w-3.5 text-[#726AF0]" /> Shared</div>
                                            <div className="text-xs text-muted-foreground">Visible to everyone in your batch — helps classmates with the same question.</div>
                                        </div>
                                    </label>
                                    <label className="flex items-start gap-3 p-3 rounded-xl border border-border cursor-pointer has-[[data-state=checked]]:border-[#726AF0]">
                                        <RadioGroupItem value="private" id="vis-private" data-testid="doubt-visibility-private" />
                                        <div>
                                            <div className="flex items-center gap-1.5 font-semibold text-sm"><Lock className="h-3.5 w-3.5" /> Private</div>
                                            <div className="text-xs text-muted-foreground">Only you and staff can see this.</div>
                                        </div>
                                    </label>
                                </RadioGroup>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button onClick={post} disabled={!form.course_id || !form.title.trim() || !form.body.trim() || posting}
                                className="mx-btn-primary" data-testid="doubt-post-submit">
                                {posting ? "Posting…" : "Post doubt"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="flex flex-wrap gap-3 items-center">
                <Select value={courseFilter || "ALL"} onValueChange={(v) => setCourseFilter(v === "ALL" ? "" : v)}>
                    <SelectTrigger className="w-52" data-testid="doubts-course-filter"><SelectValue placeholder="All courses" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ALL">All courses</SelectItem>
                        {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-44" data-testid="doubts-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>{DOUBT_STATUS_FILTERS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
                <Badge variant="secondary">{items.length} {items.length === 1 ? "doubt" : "doubts"}</Badge>
            </div>

            <div className="grid gap-3">
                {items.map(d => (
                    <Card key={d.id} className="mx-card cursor-pointer hover:shadow-mx-md transition-shadow"
                        onClick={() => navigate(`/student/doubts/${d.id}`)} data-testid={`doubt-row-${d.id}`}>
                        <CardContent className="p-5 flex flex-wrap items-start gap-4">
                            <div className="h-11 w-11 rounded-xl bg-[#F0EFFF] dark:bg-[#1C1A2E] flex items-center justify-center shrink-0">
                                <MessageCircleQuestion className="h-5 w-5 text-[#726AF0]" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{d.course_title}</div>
                                <div className="font-bold text-base mt-1 truncate">{d.title}</div>
                                <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{d.body}</div>
                                <div className="flex flex-wrap gap-2 mt-3 items-center">
                                    <DoubtStatusBadge status={d.status} />
                                    <DoubtVisibilityBadge visibility={d.visibility} />
                                    <Badge variant="outline" className="text-[10px]"><MessageSquare className="h-3 w-3 mr-1" />{d.reply_count} {d.reply_count === 1 ? "reply" : "replies"}</Badge>
                                    <span className="text-xs text-muted-foreground">{new Date(d.created_at).toLocaleDateString()}</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
                {items.length === 0 && (
                    <div className="mx-card p-12 text-center text-muted-foreground">
                        <MessageCircleQuestion className="h-10 w-10 mx-auto mb-3 opacity-40" />
                        No doubts yet. Post one if you're stuck on something.
                    </div>
                )}
            </div>
        </div>
    );
}
